import React, { useState, useMemo, useEffect } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, Typography, FormControl, Select, MenuItem, Stack,
  InputLabel, Tabs, Tab, CircularProgress, Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import * as salesCostsApi from '../../lib/api/salesCosts';
import { selectMonth } from '../../utils/calcDatetime';

const StyledTextField = styled(TextField)({
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' },
  '& input[type=number]': { MozAppearance: 'textfield', textAlign: 'right' },
});

const SalesMngCostStatsContainerNew = () => {
  // 1. 상태 관리: 2026년을 기본값으로 설정
  const [selectedYear, setSelectedYear] = useState('2026');
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [data, setData] = useState([]);

  const userStr = localStorage.getItem('user'); // 원본 가져오기
  const userInfo = userStr ? JSON.parse(userStr) : {}; // 객체로 변환

  const dynamicYearList = useMemo(() => {
    const currentYear = new Date().getFullYear(); // 시스템 날짜 기준 (현재 2026)
    const startYear = 2026;
    const endYear = currentYear; // 현재 연도까지만 노출
    
    const years = [];
    for (let y = startYear; y <= endYear; y++) {
      years.push({ value: String(y), label: `${y}년` });
    }
    return years;
  }, []); // 내년 1월 1일이 되면 자동으로 2027년까지 생성됨

  const months = Array.from({ length: 12 }, (_, i) => `${i + 1}월`);
  
  const fetchDepartments = async () => {
    try {
      // 1. 해당 년/월 데이터 조회
      const res = await salesCostsApi.getSalesCostsStaticTotalNew(selectedYear, selectedMonth + 1);
      let list = res?.data?.list || [];

      // 2. 데이터가 없으면 기본 부서 목록 조회 (DPT_UUID 기준)
      if (list.length === 0) {
        const resDept = await salesCostsApi.getSalesCostsStaticTotalDeptNew();
        list = resDept?.data?.list || [];
      }

      // 3. SQL 결과 컬럼명(대문자) 매핑
      const initialData = list.map((item) => ({
        DPT_UUID: item.dptUuid ,
        DPT_NM: item.dptNm || '부서명 없음', // 부서명 필드 유연하게 처리
        PAY_ID_COST: item.payIdCost  || 0,
        PAY_DR_COST: item.payDrCost || 0,
        PAY_DR_EXP: item.payDrExp || 0,
        PAY_CORP_COMMON: item.payCorpCommon || 0,
        PAY_SBU_CMN: item.paySbuCmn || 0,
        CRT_USR: item.crtUsr || '',
        CNG: item.cngUsr || '',
      }));
      setData(initialData);
    } catch (error) {
      console.error("Fetch Error:", error);
    } finally {
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, [selectedYear, selectedMonth]);

  const handleSave = async () => {
    try {
      // 자바 DTO(SalesMngCostNewDto)의 소문자 필드명에 정확히 매칭
      const saveDataList = data.map((item) => ({
      dptUuid: item.DPT_UUID, 
      dptNm: item.DPT_NM,
      payIdCost: Number(item.PAY_ID_COST) || 0,
      payDrCost: Number(item.PAY_DR_COST) || 0,
      payDrExp: Number(item.PAY_DR_EXP) || 0,
      payCorpCommon: Number(item.PAY_CORP_COMMON) || 0,
      paySbuCmn: Number(item.PAY_SBU_CMN) || 0,
      crtUsr: userInfo.usrUuid,
      cngUsr: userInfo.usrUuid,
    }));

      await salesCostsApi.saveSalesCostsStaticTotalNew(
        selectedYear,
        selectedMonth + 1,
        saveDataList
      );

      alert(`${selectedYear}년 ${selectedMonth + 1}월 데이터가 저장되었습니다.`);
      fetchDepartments(); 
    } catch (error) {
      console.error("저장 실패:", error);
      alert('저장 중 오류가 발생했습니다. 서버 로그를 확인해주세요.');
    }
  };

  // 값 변경 핸들러 (입력 편의성을 위해 문자열 상태 유지 후 계산 시 숫자로 변환)
  const handleChange = (index, field, value) => {
    const newData = [...data];
    // 빈 값은 빈 문자열로, 그 외엔 숫자형으로 업데이트
    newData[index][field] = value === '' ? '' : Number(value);
    setData(newData);
  };

  const stats = useMemo(() => {
    const rowTotals = data.map(row => 
      (Number(row.PAY_ID_COST) || 0) + (Number(row.PAY_DR_COST) || 0) + 
      (Number(row.PAY_DR_EXP) || 0) + (Number(row.PAY_CORP_COMMON) || 0) + (Number(row.PAY_SBU_CMN) || 0)
    );
    const colTotals = data.reduce((acc, row) => ({
      PAY_ID_COST: acc.PAY_ID_COST + (Number(row.PAY_ID_COST) || 0),
      PAY_DR_COST: acc.PAY_DR_COST + (Number(row.PAY_DR_COST) || 0),
      PAY_DR_EXP: acc.PAY_DR_EXP + (Number(row.PAY_DR_EXP) || 0),
      PAY_CORP_COMMON: acc.PAY_CORP_COMMON + (Number(row.PAY_CORP_COMMON) || 0),
      PAY_SBU_CMN: acc.PAY_SBU_CMN + (Number(row.PAY_SBU_CMN) || 0),
    }), { PAY_ID_COST: 0, PAY_DR_COST: 0, PAY_DR_EXP: 0, PAY_CORP_COMMON: 0, PAY_SBU_CMN: 0 });

    return { rowTotals, colTotals, grandTotal: rowTotals.reduce((a, b) => a + b, 0) };
  }, [data]);

  return (
    <Box sx={{ p: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          판매 관리 비용 등록 ({selectedYear}년 {selectedMonth + 1}월)
        </Typography>
        
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="year-select-label">등록 연도</InputLabel>
            <Select
              labelId="year-select-label"
              value={selectedYear}
              label="등록 연도"
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              {dynamicYearList.map((y) => (
                <MenuItem key={y.value} value={y.value}>
                  {y.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Button variant="contained" onClick={handleSave}>저장</Button>
        </Stack>
      </Stack>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs 
          value={selectedMonth} 
          onChange={(_, v) => setSelectedMonth(v)} 
          variant="fullWidth"
        >
          {months.map((month, index) => (
            <Tab key={`month-tab-${index}`} label={month} sx={{ fontWeight: 'bold' }} />
          ))}
        </Tabs>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'medium' }}>
          (단위: 원)
        </Typography>
      </Box>
     <TableContainer component={Paper} variant="outlined">
      <Table size="small" sx={{ tableLayout: 'fixed' }}>
        <TableHead sx={{ bgcolor: '#f8f9fa' }}>
          <TableRow>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>부서명</TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>간접비(원가)</TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>직접비(원가)</TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>직접비</TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>전체공통비</TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>전략사업본부(공통)</TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#eef2ff' }}>판관비 합계</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow key={row.DPT_UUID}>
              <TableCell align="center" sx={{ bgcolor: '#fafafa' }}>{row.DPT_NM}</TableCell>
              <TableCell><CostInput value={row.PAY_ID_COST} onChange={(v) => handleChange(index, 'PAY_ID_COST', v)} /></TableCell>
              <TableCell><CostInput value={row.PAY_DR_COST} onChange={(v) => handleChange(index, 'PAY_DR_COST', v)} /></TableCell>
              <TableCell><CostInput value={row.PAY_DR_EXP} onChange={(v) => handleChange(index, 'PAY_DR_EXP', v)} /></TableCell>
              <TableCell><CostInput value={row.PAY_CORP_COMMON} onChange={(v) => handleChange(index, 'PAY_CORP_COMMON', v)} /></TableCell>
              <TableCell><CostInput value={row.PAY_SBU_CMN} onChange={(v) => handleChange(index, 'PAY_SBU_CMN', v)} /></TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', pr: 2, bgcolor: '#f5f7ff' }}>
                {(stats.rowTotals[index] || 0).toLocaleString()}
              </TableCell>
            </TableRow>
          ))}

          {/* 하단 합계 행: 글자 크기는 동일하게(기본값), 굵기만 Bold 적용 */}
          <TableRow sx={{ bgcolor: '#fffde7' }}>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>전체 합계</TableCell>
            <TableCell align="right" sx={{ pr: 2, fontWeight: 'bold' }}>
              {(stats.colTotals.PAY_ID_COST || 0).toLocaleString()}
            </TableCell>
            <TableCell align="right" sx={{ pr: 2, fontWeight: 'bold' }}>
              {(stats.colTotals.PAY_DR_COST || 0).toLocaleString()}
            </TableCell>
            <TableCell align="right" sx={{ pr: 2, fontWeight: 'bold' }}>
              {(stats.colTotals.PAY_DR_EXP || 0).toLocaleString()}
            </TableCell>
            <TableCell align="right" sx={{ pr: 2, fontWeight: 'bold' }}>
              {(stats.colTotals.PAY_CORP_COMMON || 0).toLocaleString()}
            </TableCell>
            <TableCell align="right" sx={{ pr: 2, fontWeight: 'bold' }}>
              {(stats.colTotals.PAY_SBU_CMN || 0).toLocaleString()}
            </TableCell>
            {/* 최종 합계: 굵기와 색상만으로 강조 */}
            <TableCell align="right" sx={{ pr: 2, fontWeight: 'bold', color: 'primary.main' }}>
              {(stats.grandTotal || 0).toLocaleString()}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
    </Box>
  );
};

const CostInput = ({ value, onChange }) => {
  // 1. 숫자를 콤마 문자열로 변환 (보여주기용)
  const formatComma = (val) => {
    if (val === null || val === undefined || val === '') return '';
    return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // 2. 입력 시 콤마 제거 후 숫자만 부모에게 전달
  const handleInputChange = (e) => {
    const rawValue = e.target.value.replace(/,/g, ''); // 콤마 제거
    // 숫자만 입력 가능하도록 체크 (빈 값 허용)
    if (rawValue === '' || !isNaN(rawValue)) {
      onChange(rawValue === '' ? 0 : Number(rawValue)); 
    }
  };

  return (
    <StyledTextField
      fullWidth
      size="small"
      type="text"
      variant="outlined"
      value={formatComma(value)}
      onChange={handleInputChange}
      placeholder="0"
      sx={{ 
        '& .MuiOutlinedInput-root': { 
          backgroundColor: '#fff',
          height: '32px' // 1. 입력창 전체 높이 축소
        },
        '& input': { 
          textAlign: 'right', 
          fontSize: '0.875rem', // 2. 글자 크기를 테이블 텍스트와 맞춤 (14px)
          padding: '4px 8px'    // 3. 상하 여백을 줄여서 뚱뚱하지 않게 조절
        } 
      }}
    />
  );
};

export default SalesMngCostStatsContainerNew;
